import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Store, Plus } from 'lucide-react';

const STATUS_STYLES = {
  open: 'bg-green-100 text-green-700',
  applications_closed: 'bg-amber-100 text-amber-700',
  assigned: 'bg-blue-100 text-blue-700',
  completed: 'bg-gray-100 text-gray-600',
};

const emptyForm = {
  title: '', description: '', customer: '', vertical: '', scope: '',
  timeline: '', deliverables: '', budget: '', commission_note: '', skills: '',
  slots: 1, brief_text: '',
};

const AdminMarketplace = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [briefFile, setBriefFile] = useState(null);
  const [blueprintFile, setBlueprintFile] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, [statusFilter]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/marketplace/projects`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setProjects(res.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (briefFile) fd.append('brief_file', briefFile);
      if (blueprintFile) fd.append('blueprint_file', blueprintFile);
      await axios.post(`${API_URL}/api/admin/marketplace/projects`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Project posted');
      setForm(emptyForm);
      setBriefFile(null);
      setBlueprintFile(null);
      setDialogOpen(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
              <Store className="w-6 h-6 text-[#ef785b]" /> Marketplace
            </h2>
            <p className="text-[#64748B] text-sm mt-1">Post implementation projects and review partner applications.</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#ef785b] hover:bg-[#e0664a]"><Plus className="w-4 h-4 mr-1.5" /> New Project</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Post a new project</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input id="title" value={form.title} onChange={(e) => setField('title', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" rows={3} value={form.description} onChange={(e) => setField('description', e.target.value)} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label htmlFor="customer">Customer</Label>
                    <Input id="customer" value={form.customer} onChange={(e) => setField('customer', e.target.value)} /></div>
                  <div><Label htmlFor="vertical">Industry vertical</Label>
                    <Input id="vertical" value={form.vertical} onChange={(e) => setField('vertical', e.target.value)} /></div>
                </div>
                <div>
                  <Label htmlFor="brief_text">Project brief (rich text)</Label>
                  <Textarea id="brief_text" rows={3} value={form.brief_text} onChange={(e) => setField('brief_text', e.target.value)} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label htmlFor="scope">Scope</Label>
                    <Textarea id="scope" rows={2} value={form.scope} onChange={(e) => setField('scope', e.target.value)} /></div>
                  <div><Label htmlFor="deliverables">Deliverables</Label>
                    <Textarea id="deliverables" rows={2} value={form.deliverables} onChange={(e) => setField('deliverables', e.target.value)} /></div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label htmlFor="timeline">Timeline</Label>
                    <Input id="timeline" value={form.timeline} onChange={(e) => setField('timeline', e.target.value)} placeholder="e.g. 6 weeks" /></div>
                  <div><Label htmlFor="budget">Budget / Commission</Label>
                    <Input id="budget" value={form.budget} onChange={(e) => setField('budget', e.target.value)} placeholder="optional" /></div>
                  <div><Label htmlFor="slots">Slots</Label>
                    <Input id="slots" type="number" min={1} value={form.slots} onChange={(e) => setField('slots', parseInt(e.target.value || '1', 10))} /></div>
                </div>
                <div>
                  <Label htmlFor="skills">Required skills (comma-separated)</Label>
                  <Input id="skills" value={form.skills} onChange={(e) => setField('skills', e.target.value)} placeholder="e.g. Shopify, Integrations, CRM" />
                </div>
                <div>
                  <Label htmlFor="commission_note">Commission note (optional)</Label>
                  <Input id="commission_note" value={form.commission_note} onChange={(e) => setField('commission_note', e.target.value)} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label htmlFor="brief_file">Brief file (PDF/DOCX)</Label>
                    <Input id="brief_file" type="file" accept=".pdf,.docx,.doc" onChange={(e) => setBriefFile(e.target.files[0])} /></div>
                  <div><Label htmlFor="blueprint_file">Blueprint (PDF/image)</Label>
                    <Input id="blueprint_file" type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setBlueprintFile(e.target.files[0])} /></div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={submitting} className="bg-[#ef785b] hover:bg-[#e0664a]">
                    {submitting ? 'Posting...' : 'Post project'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="applications_closed">Applications Closed</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-[#64748B]">Loading...</div>
            ) : projects.length === 0 ? (
              <div className="text-center py-12 text-[#64748B]">No projects yet. Post your first project.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Customer / Vertical</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Applications</TableHead>
                    <TableHead>Slots</TableHead>
                    <TableHead>Posted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/admin/marketplace/${p.id}`)}>
                      <TableCell className="font-medium">{p.title}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{p.customer || '—'}{p.vertical ? ` · ${p.vertical}` : ''}</TableCell>
                      <TableCell>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[p.status]}`}>
                          {String(p.status).replace(/_/g, ' ')}
                        </span>
                      </TableCell>
                      <TableCell>{p.application_count}</TableCell>
                      <TableCell>{(p.assigned_partner_ids || []).length}/{p.slots}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminMarketplace;
